// Projection: file icon classification for vault UI rendering.
export type FileIconType = "pdf" | "docx" | "xlsx" | "image" | "link" | "text";

// Projection: file item for vault display and detail panel.
// Maps from: File (api-model) — transformed by useVaultData hook
export interface VaultFileItem {
  id: string;
  name: string;
  fileIcon: FileIconType;
  date: string;
  // Detail panel fields — computed in useVaultData hook
  sizeBytes: number;
  sizeFormatted: string;
  fileType: string;
  extension: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  createdAtFormatted: string;
  updatedAtFormatted: string;
  folderPath?: string;
  /** Demo / UI flags — TODO(api-model) when backend supports */
  starred?: boolean;
  shared?: boolean;
  trashed?: boolean;
  tags?: string[];
  sourceId?: "eq" | "gdrive" | "dropbox";
  clientId?: string;
}

// Projection: folder tree node for vault navigation.
// Maps from: File (api-model) — built from flat files with folderPath metadata
export interface VaultFolderNode {
  id: string;
  name: string;
  children: (VaultFolderNode | VaultFileItem)[];
  /** Demo UI */
  modified?: string;
  shared?: boolean;
  starred?: boolean;
  sourceId?: "eq" | "gdrive" | "dropbox";
  color?: string;
}

export function isFolder(node: VaultFolderNode | VaultFileItem): node is VaultFolderNode {
  return "children" in node;
}

// Projection: simplified deal card for vault sidebar.
// Maps from: Deal + DealVersion (api-model) — title extracted from DealVersion.content
export interface VaultDeal {
  id: string;
  name: string;
  type: string;
  status: string;
  value?: string;
}

// UI state types for vault navigation
export type Selection =
  | { type: "mail" }
  | { type: "meeting-notes" }
  | { type: "messages" }
  | { type: "files-equaliq"; path?: string[] }
  | { type: "files-gdrive"; path?: string[] }
  | { type: "files-dropbox"; path?: string[] }
  | { type: "deal"; dealId: string };

export type FileSourceType = "files-equaliq" | "files-gdrive" | "files-dropbox";
export type ContentType = "files" | "mail" | "meetings" | "messages";

export interface BreadcrumbSegment {
  label: string;
  path: string[];
}

// Projection: conversation item types for vault integration panels.
// TODO(api-model): These will map from real email/calendar/messaging API types when integrations are built

export interface MailItem {
  id: string;
  subject: string;
  from: string;
  preview: string;
  date: string;
  hasAttachment: boolean;
  unread: boolean;
}

export interface MeetingNoteItem {
  id: string;
  title: string;
  date: string;
  duration: string;
  attendees: string[];
  source: string;
}

export interface MessageItem {
  id: string;
  channel: string;
  sender: string;
  preview: string;
  date: string;
  source: string;
}

// Projection: domain-level input for creating a deal from the vault UI.
// Maps from: CreateDealRequestContent (api-model)
export interface CreateDealInput {
  orgId: string;
  title: string;
  description?: string;
}

// Projection: domain-level result from creating a deal.
// Maps from: CreateDealResponseContent (api-model)
export interface CreateDealResult {
  dealId: string;
}

// Projection: domain-level input for uploading a file from the vault UI.
// Maps from: CreateFileRequestContent (api-model)
export interface CreateFileInput {
  orgId: string;
  fileName: string;
  sizeBytes: number;
  fileType?: string;
  folderPath?: string;
  requestUploadUrl?: boolean;
}
